import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { LookupGetService } from '../../../service/lookup-service/lookup-get.service';
import { ListColumn, ListDataColumn } from '../../../ui/Data/ListColumn';
import { DateUtil } from '../../../ui/DateUtil';
import { environment } from '../../../../../environments/environment';
import { Art57Section } from '../../../ui/Art57Section/Art57Section';
import { Subject, takeUntil } from 'rxjs';
import { BulkService } from '../../../service/bulk-service/bulk.service';
import {
  operationEnum,
  stepperEnum,
} from '../../../common/enum/bulkExportEnum';
import { ConfirmationService } from 'primeng/api';
import { Util } from '../../../ui/Util';
import { bulkExportStatusListEnum } from '../../../common/enum/bulkExportStatusListEnum';
import { testingEnum } from '../../../common/enum/testingEnum';
import { PageChangeType } from '../../../common/enum/PageChangeTypeEnum';
import { BulkOperationSourceEnum } from '../../../ui/Node/MedicinalProductsNode/BulkOperationSourceEnum';
import { BulkSection } from '../../../ui/Art57Section/BulkSection';
import { FileUtil } from '../../../ui/FileUtil';
import { TrackerServiceComponent } from '../../../service/tracker-service/tracker.service';


@Component({
  selector: 'app-request-viewer',
  templateUrl: './request-viewer.component.html',
  styleUrl: './request-viewer.component.scss',
})
export class RequestViewerComponent {
  @Output() selectedElement: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('filterBox') filterBox!: ElementRef;
  @Input({ required: true })
  operationSource!: BulkOperationSourceEnum;

  private bulkSection!: BulkSection;
  private destroy$ = new Subject<void>();

  //actions
  cancelableStatus = bulkExportStatusListEnum.CANCELABLE;
  downloadableStatus = bulkExportStatusListEnum.DOWNLOADABLE;
  archivableStatus = bulkExportStatusListEnum.ARCHIVABLE;
  openStatus = bulkExportStatusListEnum.OPEN;

  //colors:
  queuedStatus = bulkExportStatusListEnum.QUEUED;
  inProgressStatus = bulkExportStatusListEnum.IN_PROGRESS;
  archivedStatus = bulkExportStatusListEnum.ARCHIVED;
  errorOrinterruptedStatus = bulkExportStatusListEnum.ERROR_OR_INTERRUPTED;
  canceledStatus = bulkExportStatusListEnum.CANCELLED;

  //filters - all statuses for backend:
  allStatus = bulkExportStatusListEnum.ALL;

  //colors:
  queuedStatusSet: Set<string> = new Set<string>();
  inProgressStatusSet: Set<string> = new Set<string>();
  archivedStatusSet: Set<string> = new Set<string>();
  errorOrinterruptedStatusSet: Set<string> = new Set<string>();
  canceledStatusSet: Set<string> = new Set<string>();

  //actions:
  cancelableStatusSet: Set<string> = new Set<string>();
  downloadableStatusSet: Set<string> = new Set<string>();
  archivableStatusSet: Set<string> = new Set<string>();
  openStatusSet: Set<string> = new Set<string>();

  columns: ListColumn[] = [];
  items: any[] = [];
  sortProperty: string = '';
  sortOrder = 1;
  loading = false;

  constructor(
    private injector: Injector,
    public confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.initializeBulkSection();
    this.restoreSortStateFromSection();
    this.parseStatusGroups();
    this.createColumns();
    this.subscribeStepperStepChange();
    this.subscribeRefreshDataEvent();
    this.subscribePageChangeEvent();
  }

  ngAfterViewInit(): void {
    this.setFilterTextFromCache(); // restore textbox filter from the cache
    this.focusFilterTextBoxSafely();
  }

  // initialize BulkExporSection
  private initializeBulkSection() {
    this.bulkSection = this.injector
      .get(Art57Section)
      .getSelectedSection() as BulkSection;
  }

  // restore sorting
  private restoreSortStateFromSection(): void {
    const cache = this.bulkSection.cachedRequests;

    if (cache.sortProperty) {
      this.sortProperty = cache.sortProperty;
    }
    if (cache.sortOrder === 1 || cache.sortOrder === -1) {
      this.sortOrder = cache.sortOrder;
    }
  }

  // create columns for the table
  private createColumns(): void {
    this.columns.push(
      new ListColumn('Request Title', ListDataColumn.TEXT, 'name'),
    );
    this.columns.push(
      new ListColumn('Status', ListDataColumn.TEXT, 'statusDecoded'),
    );
    this.columns.push(
      new ListColumn('Size (ZIP)', ListDataColumn.NUMBER, 'zipSizeBytes'),
    );
    this.columns.push(
      new ListColumn(
        'Progress',
        ListDataColumn.NUMBER,
        'requestProgressPercentage',
      ),
    );
    this.columns.push(
      new ListColumn(
        'Requested entities',
        ListDataColumn.NUMBER,
        'numEntities',
      ),
    );
    this.columns.push(
      new ListColumn('Created', ListDataColumn.DATETIME, 'createdStamp'),
    );
    this.columns.push(
      new ListColumn('Started', ListDataColumn.DATETIME, 'startedStamp'),
    );
    this.columns.push(
      new ListColumn('Completed', ListDataColumn.DATETIME, 'doneStamp'),
    );
  }

  //subscribe step change in the stepper
  private subscribeStepperStepChange(): void {
    this.injector
      .get(BulkService)
      .stepChange$.pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event.stepIndex === stepperEnum.secondStep) {
          this.onStepChange();
        }
      });
  }

  // subscribe refresh button event
  private subscribeRefreshDataEvent(): void {
    this.injector
      .get(BulkService)
      .refresh$.pipe(takeUntil(this.destroy$))
      .subscribe((operation) => {
        if (operation !== this.operationSource) {
          return;
        }

        this.resetManualSortAndFilterText();
        this.loadRequestsFromDbAndCacheIt('', { page: 0 });
      });
  }

  // subscribe change page button event
  private subscribePageChangeEvent(): void {
    this.injector
      .get(BulkService)
      .pageChange$.pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event.operation !== this.operationSource) {
          return;
        }

        const changeType = event.type;

        this.resetManualSort();
        const cache = this.bulkSection.cachedRequests;
        const pagination = cache.paginationControl;
        switch (changeType) {
          case PageChangeType.First: {
            // Go to first page (0)
            this.loadRequestsFromDbAndCacheIt(cache.filterText ?? '', {
              page: 0,
            });
            break;
          }

          case PageChangeType.Next: {
            // Try next page, but only commit if data exists
            const current = pagination.currentPage;
            this.loadRequestsFromDbAndCacheIt(cache.filterText ?? '', {
              page: current + 1,
              nextPageCaller: true,
            });
            break;
          }

          case PageChangeType.Previous: {
            // Previous page (avoid negative)
            const current = pagination.currentPage;
            const prev = current > 0 ? current - 1 : 0;
            this.loadRequestsFromDbAndCacheIt(cache.filterText ?? '', {
              page: prev,
            });
            break;
          }

          case PageChangeType.Cancel: {
            this.onCancel();
            break;
          }

          case PageChangeType.Download: {
            this.onDownload();
            break;
          }

          case PageChangeType.Archive: {
            this.onArchive();
            break;
          }

          case PageChangeType.Open: {
            this.onOpen();
            break;
          }
        }
      });
  }

  // trigger unsubscription and complete the subject
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //event when step is changed in the stepper
  onStepChange(): void {
    if (this.bulkSection.isStepperSecondStep()) {
      this.loadData();
    }
  }

  // load the requests either from Database or from cache
  loadData(): void {
    if (this.bulkSection.isStepperSecondStep()) {
      if (this.items.length == 0) {
        const cache = this.bulkSection.cachedRequests;
        if (cache.data) {
          this.loadRequestsFromCache();
        } else {
          this.loadRequestsFromDbAndCacheIt(cache.filterText ?? '');
        }
      } else {
        this.displayMessageForZeroMaxRecords();
      }
    }
  }

  // Execute filtered search after pressing Enter in the keyboard
  filter(event: any, text: string) {
    const cache = this.bulkSection.cachedRequests;
    if (event?.key === 'Enter' || event?.key === 'NumpadEnter') {
      this.loading = true;
      if (
        this.filterBox.nativeElement.value != cache.filterText &&
        cache.filterText != ''
      ) {
        this.resetManualSort();
      }
      this.loadRequestsFromDbAndCacheIt(text, { page: 0 }); // if you are not in the first page when executing the search again display first page
    }
  }

  // load requests records from the database and cache them on success
  private loadRequestsFromDbAndCacheIt(
    text: string,
    options?: { page?: number; nextPageCaller?: boolean },
  ): void {
    const cache = this.bulkSection.cachedRequests;
    const pagination = cache.paginationControl;

    const page = options?.page ?? pagination.currentPage;
    const nextPageCaller = options?.nextPageCaller ?? false;
    this.loading = true;

    this.injector
      .get(LookupGetService)
      .filter(this.bulkSection.request, 'cn:' + text, [
        ['type', this.allStatus],
        ['page', page.toString()],
      ])
      .subscribe(
        (data: any) => {
          // If this was triggered by "Next page" and the next page is empty
          // In this case, do NOT advance the page index and keep hasNextPage as false
          if (nextPageCaller && (!data || data.length === 0)) {
            this.loading = false;
            this.injector
              .get(Art57Section)
              .showWarningTooltipMessage('No results found');
            this.bulkSection.cachedRequests = {
              ...cache,
              hasNextPage: false,
            };
            return;
          }

          // Otherwise
          this.items = (data || []).map((it: any) => this.annotateItem(it));
          this.bulkSection.cachedRequests = {
            ...cache,
            data: this.items,
            filterText: text,
            hasNextPage:
              this.items.length === environment.maxNumberRowsReturned,
          };

          // Commit the page index
          pagination.currentPage = page;

          this.applyCurrentSort();
          this.displayMessageForZeroMaxRecords();
          this.loading = false;
        },
        (err) => {
          this.loading = false;
          this.injector
            .get(Art57Section)
            .showErrorTooltipMessage('Error loading requests');
          throw err;
        },
      );
  }

 

   // Load action from the database and cache it on success
  private loadActionFromDbAndCacheIt(): void {
    const item = this.bulkSection.selectedItem;
    const bulkUpdate = this.injector.get(Art57Section).bulkupdate;

    this.loading = true;
    bulkUpdate.clearActionCache();
    this.injector.get(Art57Section).showSuccessTooltipMessage('Retrieving information');
    bulkUpdate.actionSetFilename = FileUtil.removeExtension(FileUtil.decodeBase64(item.code));
    this.injector!.get(BulkService)
      .operationRequest(item, operationEnum.open)
      .subscribe(
        (response: any) => {
          (response.body as Blob).text().then((text) => {
            const json = JSON.parse(text);
            bulkUpdate.actionCache.tuplesJson = json;
            bulkUpdate.extractActionOption();
            bulkUpdate.actionCache.actionEnabled = true;
            this.injector.get(Art57Section).showSuccessTooltipMessage('Information Retrieved');
            this.injector.get(TrackerServiceComponent).stop(this.injector.get(BulkService).actionTrackerKey);
            this.loading = false;
          });
        },
        (err) => {
          this.loading = false;
          bulkUpdate.actionSetFilename = '';
          this.injector
            .get(Art57Section)
            .showErrorTooltipMessage('Error loading action');

          throw err;
        },
      );
  }

  // load requests records from the cache
  private loadRequestsFromCache(): void {
    this.loading = true;
    const cache = this.bulkSection.cachedRequests;
    this.items = (cache.data ?? []) as any[];

    // Re-apply current sort if user selected one
    this.applyCurrentSort();

    this.displayMessageForZeroMaxRecords();
    this.loading = false;
  }

  // set filter texbox with the value from the cache
  private setFilterTextFromCache(): void {
    const cache = this.bulkSection.cachedRequests;
    if (cache.filterText != null) {
      try {
        this.filterBox.nativeElement.value = cache.filterText;
      } catch (e) {}
    }
  }

  // focus filter texbox
  private focusFilterTextBoxSafely(): void {
    setTimeout(() => {
      try {
        this.filterBox.nativeElement.focus();
      } catch (e) {}
    }, 0);
  }

  //clears the manual sort and the search filter components and their cache
  private resetManualSortAndFilterText() {
    this.bulkSection.clearRequestsCache();
    //reset sort
    this.resetManualSort();
    //reset filter text
    if (this.filterBox) {
      this.filterBox.nativeElement.value = '';
    }
  }

  // clear manual sort of the columns and its cache
  private resetManualSort(): void {
    this.bulkSection.clearRequestsSortCache();

    this.sortProperty = '';
    this.sortOrder = 1;
  }

  // parse the groups
  private parseStatusGroups(): void {
    //actions
    this.cancelableStatusSet = new Set(
      (this.cancelableStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    this.downloadableStatusSet = new Set(
      (this.downloadableStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    this.archivableStatusSet = new Set(
      (this.archivableStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    //colors
    this.queuedStatusSet = new Set(
      (this.queuedStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    this.inProgressStatusSet = new Set(
      (this.inProgressStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    this.archivedStatusSet = new Set(
      (this.archivedStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    this.errorOrinterruptedStatusSet = new Set(
      (this.errorOrinterruptedStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    this.canceledStatusSet = new Set(
      (this.canceledStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    this.openStatusSet = new Set(
      (this.openStatus || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }

  // annotate items in the table with another values such as request status, allowed actions, request color
  private annotateItem(item: any): any {
    const status = item?.status;
    const key =
      status === null || status === undefined ? '' : String(status).trim();
    const isCancelable = this.cancelableStatusSet.has(key);
    const isDownloadable = this.downloadableStatusSet.has(key);
    const isArchivable = this.archivableStatusSet.has(key);
    const isOpen = this.openStatusSet.has(key);

    return {
      ...item,
      isCancelable,
      isDownloadable,
      isArchivable,
      isQueued: this.queuedStatusSet.has(key),
      isInProgress: this.inProgressStatusSet.has(key),
      isArchived: this.archivedStatusSet.has(key),
      isErrorOrInterrupted: this.errorOrinterruptedStatusSet.has(key),
      isCanceled: this.canceledStatusSet.has(key),
      isOpen: this.openStatusSet.has(key),
      hasActions: isCancelable || isDownloadable || isArchivable || isOpen,
    };
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') || '';
    let cleanText = pastedText
      .split(/\r?\n/) // separa por líneas
      .map((s) => s.replace(/"/g, '')) // elimina comillas dobles
      .map((s) => s.trim()) // quita tabs y espacios delante/detrás
      .filter(Boolean) // elimina líneas vacías
      .join('|'); // únelas con |

    const target = event.target as HTMLTextAreaElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    if (cleanText.lastIndexOf('|') === cleanText.length - 1) {
      cleanText = cleanText.substring(0, cleanText.length - 1);
    }

    this.filterBox.nativeElement.value =
      this.filterBox.nativeElement.value.substring(0, start) +
      cleanText +
      this.filterBox.nativeElement.value.substring(end);

    setTimeout(() => {
      target.selectionStart = target.selectionEnd = start + cleanText.length;
    });
  }

  // Display the warning messages for the records - 0 records, max. records
  displayMessageForZeroMaxRecords(): void {
    this.displayMessageForZeroRecords();
    this.displayMessageForMaxRecords();
  }

  // Display the warning message max. 50 records is displayed
  displayMessageForMaxRecords(): void {
    if (this.bulkSection.isStepperSecondStep()) {
      if (this.items.length >= environment.maxNumberRowsReturned) {
        this.injector!.get(Art57Section).showWarningTooltipMessage(
          `Only ${environment.maxNumberRowsReturned} results were retrieved.  Please refine your search criteria.`,
          undefined,
          undefined,
          testingEnum.maxResultsRetrievedToastMsg,
        );
      }
    }
  }

  // Display the warning message 0 records is displayed
  displayMessageForZeroRecords(): void {
    if (this.bulkSection.isStepperSecondStep()) {
      if (this.items.length == 0) {
        this.injector!.get(Art57Section).showWarningTooltipMessage(
          'There are no items for current filter',
        );
      }
    }
  }

  // handlers for cancel button
  onCancel() {
    const item = this.bulkSection.selectedItem;

    this.confirmationService.confirm({
      header: 'Confirm Cancellation',
      message:
        'Request: ' +
        item.name +
        '<br>Date: ' +
        item.createdStamp +
        '<br>Requested entities: ' +
        (item.numEntities ?? '') +
        '<br>Status: ' +
        item.statusDecoded,
      icon: 'pi pi-question-circle',
      acceptLabel: 'OK',
      rejectLabel: 'Cancel',
      accept: () => {
        this.injector!.get(BulkService)
          .operationRequest(item, operationEnum.cancel)
          .subscribe({
            next: (data) => {
              this.injector
                .get(Art57Section)
                .showSuccessTooltipMessage('Refreshing results');
              this.injector
                .get(BulkService)
                .notifyRefresh(this.operationSource);
            },
          });
      },
    });
  }

  // handlers for downlaod button
  onDownload() {
    const item = this.bulkSection.selectedItem;

    this.injector!.get(BulkService)
      .operationRequest(item, operationEnum.download)
      .subscribe({
        next: (response) => {
          const link = document.createElement('a');
          let url: string;
          url = URL.createObjectURL(
            new Blob([response.body], { type: 'application/zip' }),
          );

          link.href = url;
          link.download =
            response.headers
              .get('Content-Disposition')
              .match(/filename\*?=(?:UTF-8''|")?([^;"']+)/i)?.[1] ??
            'undefined';

          this.injector
            .get(Art57Section)
            .showTooltipMessage({
              severity: 'success',
              detail: 'Request downloaded successfully',
              life: environment.successElapsedTime,
            });

          link.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  // handlers for archive button
  onArchive() {
    const item = this.bulkSection.selectedItem;

    this.confirmationService.confirm({
      header: 'Confirm deletion',
      message:
        'Request: ' +
        item.name +
        '<br>Date: ' +
        item.startedStamp +
        '<br>Requested entities: ' +
        (item.numEntities ?? '') +
        '<br>Status: ' +
        item.statusDecoded,
      icon: 'pi pi-question-circle',
      acceptLabel: 'OK',
      rejectLabel: 'Cancel',
      accept: () => {
        this.injector!.get(BulkService)
          .operationRequest(item, operationEnum.archive)
          .subscribe({
            next: (data) => {
              this.injector
                .get(Art57Section)
                .showSuccessTooltipMessage('Refreshing results');
              this.injector
                .get(BulkService)
                .notifyRefresh(this.operationSource);
            },
          });
      },
      reject: () => {
        //do nothing
      },
    });
  }

  // handlers for downlaod button
  onOpen() {
    this.loadActionFromDbAndCacheIt();
  }

  sortBy(column: ListColumn) {
    this.sortOrder =
      column.getSourceName() === this.sortProperty ? this.sortOrder * -1 : 1;
    this.sortProperty = column.getSourceName();
    this.bulkSection.cachedRequests = {
      ...this.bulkSection.cachedRequests,
      sortProperty: this.sortProperty,
      sortOrder: this.sortOrder,
    };
    this.applyCurrentSort();
  }

  private applyCurrentSort(): void {
    if (!this.sortProperty) {
      return;
    }

    const column = this.columns.find(
      (c) => c.getSourceName() === this.sortProperty,
    );
    if (!column) {
      return;
    }

    this.items = [
      ...this.items.sort((a: any, b: any) => {
        let result = 0;

        const valueA = a[this.sortProperty];
        const valueB = b[this.sortProperty];

        if (
          column.getDataType() === ListDataColumn.DATE ||
          column.getDataType() === ListDataColumn.DATETIME
        ) {
          const timeA = valueA ? new Date(valueA).getTime() : 0;
          const timeB = valueB ? new Date(valueB).getTime() : 0;
          result = timeA < timeB ? -1 : timeA > timeB ? 1 : 0;
        } else if (column.getDataType() === ListDataColumn.NUMBER) {
          const numA = valueA == null ? 0 : Number(valueA);
          const numB = valueB == null ? 0 : Number(valueB);
          result = numA < numB ? -1 : numA > numB ? 1 : 0;
        } else {
          // Case‑insensitive text compare
          const strA = (valueA ?? '').toString().toLowerCase();
          const strB = (valueB ?? '').toString().toLowerCase();
          result = strA < strB ? -1 : strA > strB ? 1 : 0;
        }

        return result * this.sortOrder;
      }),
    ];
  }

  sortIcon(property: string) {
    if (property === this.sortProperty) {
      return this.sortOrder === 1 ? '↑' : '↓';
    }
    return '';
  }

  displayValue(item: any, column: ListColumn): string {
    const value = item[column.getSourceName()];

    if (value == null) {
      return '';
    }

    const dataType = column.getDataType();

    // Special handling for Status text (statusDecoded)
    if (column.getSourceName() === 'statusDecoded') {
      return Util.decodeStatusBulkExport(item);
    }

    switch (dataType) {
      case ListDataColumn.DATE:
        return DateUtil.getDateFormat(value);

      case ListDataColumn.DATETIME:
        return String(value).includes('*')
          ? value
          : DateUtil.getDateTimeFormat(value);

      case ListDataColumn.NUMBER:
        switch (column.getSourceName()) {
          case 'zipSizeBytes':
            // bytes -> human readable (B, KB, MB)
            return Util.formatFileSize(value, 0, true);
          case 'requestProgressPercentage':
            // Show percentage only if item is queued or in progress, e.g. 95 -> 95%
            if (item.isQueued || item.isInProgress || item.isDownloadable) {
              return Util.formatProgress(value);
            }
            return '';
          default:
            return String(value);
        }

      default:
        return String(value);
    }
  }

  getMaxNumberRowsReturned(): number {
    return environment.maxNumberRowsReturned;
  }

  getColumnsLength() {
    return this.columns.length+1;
  }

  getPlaceHolderTemplate(): string {
    return this.bulkSection.placeholderRequest;
  }

  getPlaceHolderToolTipTemplate(): string {
    return (
      this.getPlaceHolderTemplate() +
      '. Type any character and press [ENTER] to execute the search'
    );
  }
}
